import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  Injectable,
} from '@angular/core';
import { ConfirmationModalComponent } from '@components/Modals/modal-confirmation';

export type PromptResult = 'confirm' | 'keep' | 'cancel';

@Injectable({
  providedIn: 'root',
})
export class PromptService {
  private host!: HTMLElement;

  constructor(
    private appRef: ApplicationRef,
    private envInjector: EnvironmentInjector
  ) {}

  registerHost(element: HTMLElement) {
    this.host = element;
  }

  /**
   * Confirmation simple 2 boutons (confirm / cancel)
   * Rétrocompatible avec l'existant → retourne toujours un boolean
   */
  askForConfirmation(
    title: string,
    message: string,
    confirmButtonText: string = 'common.confirm',
    cancelButtonText: string = 'common.cancel'
  ): Promise<boolean> {
    return this.openModal({
      title,
      message,
      confirmButtonText,
      cancelButtonText,
    }).then(result => result === 'confirm');
  }

  /**
   * Confirmation étendue 3 boutons (confirm / keep / cancel)
   * Retourne un PromptResult
   */
  askForConfirmationWithKeep(
    title: string,
    message: string,
    confirmButtonText: string = 'common.confirm',
    keepButtonText: string = 'common.keep',
    cancelButtonText: string = 'common.cancel'
  ): Promise<PromptResult> {
    return this.openModal({
      title,
      message,
      confirmButtonText,
      keepButtonText,
      cancelButtonText,
    });
  }

  private openModal(options: {
    title: string;
    message: string;
    confirmButtonText: string;
    keepButtonText?: string;
    cancelButtonText: string;
  }): Promise<PromptResult> {
    return new Promise(resolve => {
      const componentRef: ComponentRef<ConfirmationModalComponent> =
        createComponent(ConfirmationModalComponent, {
          environmentInjector: this.envInjector,
        });

      componentRef.instance.title = options.title;
      componentRef.instance.description = options.message;
      componentRef.instance.confirmButtonText = options.confirmButtonText;
      componentRef.instance.cancelButtonText = options.cancelButtonText;

      // Bouton "keep" optionnel
      if (options.keepButtonText) {
        componentRef.instance.keepButtonText = options.keepButtonText;
        componentRef.instance.onKeep = () => {
          resolve('keep');
          this.destroy(componentRef);
        };
      }

      componentRef.instance.onConfirm = () => {
        resolve('confirm');
        this.destroy(componentRef);
      };

      componentRef.instance.onCancel = () => {
        resolve('cancel');
        this.destroy(componentRef);
      };

      this.appRef.attachView(componentRef.hostView);
      this.host.appendChild(componentRef.location.nativeElement);
    });
  }

  private destroy(componentRef: ComponentRef<any>) {
    this.appRef.detachView(componentRef.hostView);
    componentRef.destroy();
  }
}
